import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { HardHat, Check, X, MessageSquare, Send, FileText, Clock, Euro, MapPin, Users, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BudgetPortalPage() {
  const { linkId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commentAuthor, setCommentAuthor] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [actionDone, setActionDone] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/budget-portal/${linkId}`).then(r => { setData(r.data); setLoading(false); }).catch(() => { setError('Orcamento nao encontrado'); setLoading(false); });
  }, [linkId]);

  const handleAccept = async () => {
    try { await axios.post(`${API}/api/budget-portal/${linkId}/accept`); setActionDone('aprovado'); setData(d => ({ ...d, budget: { ...d.budget, status: 'aprovado' } })); } catch {}
  };

  const handleReject = async () => {
    try { await axios.post(`${API}/api/budget-portal/${linkId}/reject`, { reason: rejectReason }); setActionDone('rejeitado'); setShowReject(false); setData(d => ({ ...d, budget: { ...d.budget, status: 'rejeitado' } })); } catch {}
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    try {
      const { data: c } = await axios.post(`${API}/api/budget-portal/${linkId}/comment`, { text: commentText, author: commentAuthor || 'Cliente' });
      setData(d => ({ ...d, budget: { ...d.budget, client_comments: [...(d.budget.client_comments || []), c] } }));
      setCommentText('');
    } catch {}
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#f7f8fa' }}><p>A carregar...</p></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#f7f8fa' }}><div className="pro-card-padded text-center p-8"><AlertTriangle size={40} className="mx-auto mb-3 text-red-500" /><p className="font-semibold">{error}</p></div></div>;

  const b = data.budget;
  const co = data.company || {};
  const isDecided = b.status === 'aprovado' || b.status === 'rejeitado';

  return (
    <div className="min-h-screen p-4" style={{ background: '#f7f8fa' }}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="rounded-t-lg p-5 text-white" style={{ background: '#1a3a5c' }}>
          <div className="flex items-center gap-3">
            <HardHat size={28} />
            <div>
              <h1 className="text-lg font-bold">{co.name || 'PRUMO'}</h1>
              <p className="text-xs opacity-75">{[co.address, co.phone, co.email].filter(Boolean).join(' | ')}</p>
            </div>
          </div>
        </div>

        {/* Status bar */}
        <div className={`px-5 py-3 text-sm font-semibold text-center ${b.status === 'aprovado' ? 'bg-green-100 text-green-700' : b.status === 'rejeitado' ? 'bg-red-100 text-red-700' : 'bg-blue-50 text-blue-700'}`} data-testid="budget-portal-status">
          {b.status === 'aprovado' ? 'Orcamento Aprovado' : b.status === 'rejeitado' ? 'Orcamento Rejeitado' : 'Orcamento Pendente de Aprovacao'}
        </div>

        <div className="bg-white rounded-b-lg shadow-sm">
          {/* Budget info */}
          <div className="p-5 border-b" style={{ borderColor: '#e5e7eb' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold" style={{ color: '#1a3a5c' }}>Orcamento #{b.id?.slice(0, 8).toUpperCase()}</h2>
              <span className="text-xs font-medium" style={{ color: '#64748b' }}>Versao {b.version || 1}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div className="p-2.5 rounded-md" style={{ background: '#f7f9fc' }}>
                <div className="flex items-center gap-1 mb-1"><Euro size={12} style={{ color: '#1a3a5c' }} /><span className="text-[10px] font-medium" style={{ color: '#64748b' }}>Total</span></div>
                <p className="text-sm font-bold" style={{ color: '#1a3a5c' }}>{(b.total || 0).toLocaleString('pt-PT')} EUR</p>
              </div>
              <div className="p-2.5 rounded-md" style={{ background: '#f7f9fc' }}>
                <div className="flex items-center gap-1 mb-1"><Clock size={12} style={{ color: '#64748b' }} /><span className="text-[10px] font-medium" style={{ color: '#64748b' }}>Prazo</span></div>
                <p className="text-sm font-bold">{b.estimated_days || '?'} dias</p>
              </div>
              <div className="p-2.5 rounded-md" style={{ background: '#f7f9fc' }}>
                <div className="flex items-center gap-1 mb-1"><Users size={12} style={{ color: '#64748b' }} /><span className="text-[10px] font-medium" style={{ color: '#64748b' }}>Equipa</span></div>
                <p className="text-sm font-bold">{b.estimated_workers || '?'} func.</p>
              </div>
              <div className="p-2.5 rounded-md" style={{ background: '#f7f9fc' }}>
                <div className="flex items-center gap-1 mb-1"><MapPin size={12} style={{ color: '#64748b' }} /><span className="text-[10px] font-medium" style={{ color: '#64748b' }}>Local</span></div>
                <p className="text-xs font-medium truncate">{b.location || '-'}</p>
              </div>
            </div>
            {b.description && <p className="text-xs" style={{ color: '#64748b' }}>{b.description}</p>}
          </div>

          {/* Items table */}
          {b.items?.length > 0 && (
            <div className="p-5 border-b" style={{ borderColor: '#e5e7eb' }}>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5" style={{ color: '#1a3a5c' }}><FileText size={14} /> Itens do Orcamento</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead><tr className="text-left" style={{ background: '#f1f5f9' }}>
                    <th className="p-2 font-semibold">#</th><th className="p-2 font-semibold">Descricao</th><th className="p-2 font-semibold text-center">Qtd</th><th className="p-2 font-semibold text-right">P. Unit.</th><th className="p-2 font-semibold text-right">Subtotal</th>
                  </tr></thead>
                  <tbody>
                    {b.items.map((it, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td className="p-2">{i+1}</td><td className="p-2">{it.descricao}</td><td className="p-2 text-center">{it.quantidade} {it.unidade}</td><td className="p-2 text-right">{(it.preco_unitario || 0).toFixed(2)} EUR</td><td className="p-2 text-right font-semibold">{(it.subtotal || 0).toFixed(2)} EUR</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-right mt-2 p-2 rounded" style={{ background: '#f1f5f9' }}>
                <span className="text-base font-bold" style={{ color: '#1a3a5c' }}>Total: {(b.total || 0).toLocaleString('pt-PT')} EUR</span>
              </div>
            </div>
          )}

          {/* AI suggestions for client */}
          {(b.ai_suggestions?.sugestoes?.length > 0 || b.ai_suggestions?.cuidados?.length > 0) && (
            <div className="p-5 border-b" style={{ borderColor: '#e5e7eb' }}>
              {b.ai_suggestions.sugestoes?.length > 0 && (
                <div className="mb-3"><h4 className="text-xs font-semibold mb-1" style={{ color: '#1a3a5c' }}>Notas Tecnicas</h4>
                  {b.ai_suggestions.sugestoes.slice(0, 4).map((s, i) => <p key={i} className="text-xs" style={{ color: '#64748b' }}>- {s}</p>)}
                </div>
              )}
              {b.ai_suggestions.cuidados?.length > 0 && (
                <div><h4 className="text-xs font-semibold mb-1" style={{ color: '#C67A09' }}>Cuidados de Seguranca</h4>
                  {b.ai_suggestions.cuidados.slice(0, 4).map((c, i) => <p key={i} className="text-xs" style={{ color: '#64748b' }}>- {c}</p>)}
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          {!isDecided && (
            <div className="p-5 border-b" style={{ borderColor: '#e5e7eb' }}>
              {actionDone ? (
                <div className={`p-4 rounded-md text-center font-semibold ${actionDone === 'aprovado' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {actionDone === 'aprovado' ? 'Orcamento aprovado com sucesso!' : 'Orcamento rejeitado.'}
                </div>
              ) : showReject ? (
                <div className="space-y-3">
                  <p className="text-sm font-semibold">Motivo da rejeicao (opcional):</p>
                  <textarea className="w-full border rounded p-2 text-sm" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Descreva o motivo..." />
                  <div className="flex gap-2">
                    <button data-testid="confirm-reject-btn" onClick={handleReject} className="flex-1 p-2.5 rounded-md text-sm font-semibold text-white" style={{ background: '#e53e3e' }}>Confirmar Rejeicao</button>
                    <button onClick={() => setShowReject(false)} className="px-4 p-2.5 rounded-md text-sm border">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button data-testid="accept-budget-btn" onClick={handleAccept} className="flex-1 p-3 rounded-md text-sm font-semibold text-white flex items-center justify-center gap-2" style={{ background: '#0D7C3E' }}>
                    <Check size={16} /> Aceitar Orcamento
                  </button>
                  <button data-testid="reject-budget-btn" onClick={() => setShowReject(true)} className="flex-1 p-3 rounded-md text-sm font-semibold border flex items-center justify-center gap-2" style={{ color: '#e53e3e', borderColor: '#e53e3e' }}>
                    <X size={16} /> Rejeitar
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Comments */}
          <div className="p-5">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5" style={{ color: '#1a3a5c' }}><MessageSquare size={14} /> Comentarios</h3>
            {(b.client_comments || []).length > 0 && (
              <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                {(b.client_comments || []).map(c => (
                  <div key={c.id} className="p-2.5 rounded-md" style={{ background: '#f7f9fc' }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold">{c.author}</span>
                      <span className="text-[10px]" style={{ color: '#64748b' }}>{c.created_at?.split('T')[0]}</span>
                    </div>
                    <p className="text-xs">{c.text}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <input data-testid="portal-comment-author" className="w-full border rounded p-2 text-sm" placeholder="Seu nome" value={commentAuthor} onChange={e => setCommentAuthor(e.target.value)} />
              <div className="flex gap-2">
                <input data-testid="portal-comment-text" className="flex-1 border rounded p-2 text-sm" placeholder="Escrever comentario..." value={commentText} onChange={e => setCommentText(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleComment()} />
                <button data-testid="portal-send-comment" onClick={handleComment} className="p-2 rounded-md text-white" style={{ background: '#1a3a5c' }}><Send size={16} /></button>
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-[10px] mt-4" style={{ color: '#94a3b8' }}>Portal do Cliente | {co.name || 'PRUMO'} | PRUMO software | Criado por: Rui Guimaraes</p>
      </div>
    </div>
  );
}
